/* eslint-disable camelcase */
/* eslint-disable react/display-name */
/**
 * @file PatientsManagementView.jsx
 * @author Fernando Ruano
 * @description Patients Management View Page
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Pagination, Input } from 'antd';
import { SearchOutlined, FileSearchOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';

// Helpers
import { sortString } from '@nephro/commons/helpers/utils';
import {
  setStorageItem,
  getStorageItem
} from '@nephro/commons/helpers/storage-helper';

// Components
import ManagementCrud from '@nephro/components/ManagementCrud';

// Styles
import styles from './styles.module.css';

/**
 * Patients Management View
 * @param { Object } patientActions - Patient Actions.
 * @return { Node } React node.
 */
function PatientsManagementView({ patientActions }) {
  const DEFAULT_FILTER_VALUE = ' ';
  const [rowData, setRowData] = React.useState([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [filter, setFilter] = React.useState(DEFAULT_FILTER_VALUE);
  const [totalItems, setTotalItems] = React.useState(0);
  const { t: translate } = useTranslation();
  const history = useHistory();
  const LIMIT = 10;

  const { getFilterPatients, deletePatientById } = patientActions;

  // Set data response
  const onGetRowData = (_, response) => {
    const content = response?.data?.content;
    if (content) {
      if (response.data.total_elements !== totalItems) {
        setTotalItems(response.data.total_elements);
      }
      const data = content.map((itm) => {
        return {
          ...itm,
          patient_status: itm?.patient_status
            ? translate(`patientMg.${itm?.patient_status}`)
            : translate(`patientMg.inactivo`),
          tipoDocumento: itm?.identification_type?.name
        };
      });
      setRowData(data);
    }
  };

  // Get patients list
  const onGetPatients = async (page = 1, filterWord = ' ') => {
    const clinicId = await getStorageItem('clinicId');
    getFilterPatients(
      { paged: true, page, limit: LIMIT, filter: filterWord, clinicId },
      onGetRowData
    );
  };

  React.useEffect(() => {
    onGetPatients(0, filter);
  }, [setRowData, totalItems]);

  const onGetRowDataRecords = (_, response) => {
    if (response?.data) {
      setStorageItem('patientInfo', {
        id_patient: response.data.content[0].id_patient,
        first_name: response.data.content[0].first_name,
        last_name: response.data.content[0].last_name,
        id_document: response.data.content[0].id_document
      });
      history.push(`/main/patient-blood-presure`);
    }
  };

  const onGetPatientsRecords = async (page = 0, filterWord = ' ') => {
    const clinicId = await getStorageItem('clinicId');
    getFilterPatients(
      { paged: true, page, limit: 10, filter: filterWord, clinicId },
      onGetRowDataRecords
    );
  };

  const handleDetailClick = async (item) => {
    setStorageItem('adminInfo', {
      diagnostico_diabetes: 'SI',
      diagnostico_hipertension: 'SI'
    });
    onGetPatientsRecords(0, item.id_document);
  };

  // Table columns
  const columns = [
    {
      title: '',
      dataIndex: 'id_document',
      ellipsis: true,
      // eslint-disable-next-line react/display-name
      render: (_, item) => {
        return (
          <div>
            <button onClick={() => handleDetailClick(item)}>
              <a>
                <div className="flex flex-auto justify-center items-center">
                  <FileSearchOutlined />
                  Ver registros
                </div>
              </a>
            </button>
          </div>
        );
      }
    },
    {
      title: translate('patientMg.name'),
      dataIndex: 'first_name',
      sorter: sortString('first_name')
    },
    {
      title: translate('patientMg.lastName'),
      dataIndex: 'last_name',
      sorter: sortString('last_name')
    },
    {
      title: translate('patientMg.documentType'),
      dataIndex: 'tipoDocumento',
      sorter: sortString('tipoDocumento'),
      ellipsis: true
    },
    {
      title: 'ID',
      dataIndex: 'id_document',
      sorter: sortString('id_document')
    },
    {
      title: translate('patientMg.email'),
      dataIndex: 'email',
      sorter: sortString('email')
    },
    {
      title: translate('patientMg.phoneNumber'),
      dataIndex: 'contact_phone_number',
      sorter: sortString('contact_phone_number')
    },
    {
      title: translate('patientMg.clinic'),
      dataIndex: 'aseguradora',
      sorter: sortString('aseguradora')
    },
    {
      title: translate('patientMg.insurance'),
      dataIndex: 'centro',
      sorter: sortString('centro')
    },
    {
      title: translate('patientMg.modality'),
      dataIndex: 'modalidad',
      sorter: sortString('modalidad')
    }
  ];

  const onSearchFilter = (e) => {
    const { value } = e.target;
    if (value.length >= 3) {
      setFilter(value);
      onGetPatients(0, value);
      setCurrentPage(1);
    } else if (value.length === 0) {
      setCurrentPage(1);
      setFilter(DEFAULT_FILTER_VALUE);
      onGetPatients(0, DEFAULT_FILTER_VALUE);
    }
  };

  const onChangePage = (page) => {
    setCurrentPage(page);
    onGetPatients(page - 1, filter);
  };

  return (
    <div className="h-full">
      {rowData && (
        <div className="flex flex-wrap">
          <div className="sm:w-full md:w-1/2 mt-4 ml-5">
            {
              <Input
                onChange={(e) => onSearchFilter(e)}
                prefix={
                  <SearchOutlined
                    style={{
                      fontSize: 16,
                      color: 'var(--color-grayFontInput)'
                    }}
                  />
                }
                placeholder={translate('commons.search')}
                allowClear
                className={styles.inputSearch}
              />
            }
          </div>
          <div className="w-full overflow-x-scroll">
            <ManagementCrud
              getItemsArray={() => onGetPatients(currentPage, filter)}
              deleteItemById={deletePatientById}
              columns={columns}
              customMapDataAfterDelete={onGetRowData}
              deleteConfirmText={'patientMg.deletePatient'}
              itemIdKey={'id_patient'}
              rowData={rowData}
              withDefaultActions={false}
              search={false}
            />
          </div>
          <div className="w-full mt-4 text-center">
            {totalItems > 0 && (
              <Pagination
                onChange={(page) => onChangePage(page)}
                showTotal={(total, range) => (
                  <span className="text-brandNavy">
                    {`${range[0]}-${range[1]} ${translate(
                      'commons.of'
                    )} ${total} items`}
                  </span>
                )}
                showSizeChanger={false}
                hideOnSinglePage
                defaultPageSize={LIMIT}
                defaultCurrent={currentPage}
                current={currentPage}
                total={totalItems}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Component definition of proptypes.
 */
PatientsManagementView.propTypes = {
  authActions: PropTypes.shape({
    recoverPassword: PropTypes.func.isRequired
  }).isRequired,
  patientActions: PropTypes.shape({
    getFilterPatients: PropTypes.func.isRequired,
    deletePatientById: PropTypes.func.isRequired,
    createPatientOkta: PropTypes.func.isRequired,
    updatePatient: PropTypes.func.isRequired,
    getPatientStatusArray: PropTypes.func.isRequired
  }).isRequired
};

export default PatientsManagementView;
